// src/components/pages/Plans.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Icon from "../ui/Icon";
import PaymentModal from "../generics/PaymentModal";

import axiosInstance from "../../utils/AxiosInstance";

/**
 * Plans / pricing page.
 *
 * Three modes depending on the user's current subscription:
 *
 *   1. Not logged in / no active subscription → each tier card shows
 *      "Start subscription"; clicking opens the existing PaymentModal
 *      for the standard create flow.
 *
 *   2. Has an active subscription → each tier card shows:
 *        - Current tier: a "Current plan" badge, button disabled
 *        - Higher tier than current: "Upgrade to X"
 *        - Lower tier than current: "Downgrade to X"
 *      Clicking either opens a confirmation modal that explains what
 *      will happen (Stripe proration vs. PayPal approval flow), then
 *      calls POST /site/subscription/change-plan. The response either
 *      completes silently (Stripe) or redirects to PayPal's approve
 *      URL (PayPal).
 *
 *   3. One-time credit packs are always buyable regardless of mode.
 *      They never change the user's tier — they just top up credits.
 *
 * The pricing copy is intentionally feature-led on subscriptions and
 * volume-led on one-time packs. There is no free tier.
 */

const TIER_RANK = { PLUS: 1, PRO: 2, ELITE: 3 };

const SUBSCRIPTION_PLANS = [
  {
    key: "PLUS",
    title: "Plus",
    price: "$5.99",
    priceSuffix: "/mo",
    tagline: "30 credits per month",
    benefits: [
      "30 credits monthly",
      "Powerball & Mega Millions AI picks",
      "Scratch-Off Intelligence — one state",
      "Basic scratch-off rankings",
      "Ticket-price filters",
      "Remaining prize strength",
    ],
    kind: "SUBSCRIPTION",
  },
  {
    key: "PRO",
    title: "Pro",
    price: "$10.99",
    priceSuffix: "/mo",
    tagline: "60 credits per month",
    benefits: [
      "60 credits monthly",
      "Everything in Plus",
      "Multi-state Scratch-Off Intelligence",
      "Advanced scratch score",
      "Scratch-off alerts",
      "Historical trend charts",
    ],
    kind: "SUBSCRIPTION",
    featured: true,
  },
  {
    key: "ELITE",
    title: "Elite",
    price: "$19.99",
    priceSuffix: "/mo",
    tagline: "200 credits per month",
    benefits: [
      "200 credits monthly",
      "Everything in Pro",
      "Full scratch-off analytics",
      "Scratch-Off Risk & Value Reports",
      "Priority access to new features",
    ],
    kind: "SUBSCRIPTION",
  },
];

const ONE_TIME_PLANS = [
  { key: "SINGLE",   title: "Single",   price: "$1",  tagline: "1 credit · 5 games",        benefits: ["1 credit",   "Never expires"], kind: "ONE_TIME" },
  { key: "BASIC",    title: "Basic",    price: "$5",  tagline: "10 credits · 50 games",     benefits: ["10 credits", "Never expires"], kind: "ONE_TIME" },
  { key: "GOLD",     title: "Gold",     price: "$20", tagline: "50 credits · 250 games",    benefits: ["50 credits", "Never expires"], kind: "ONE_TIME" },
  { key: "PLATINUM", title: "Platinum", price: "$50", tagline: "200 credits · 1,000 games", benefits: ["200 credits","Never expires"], kind: "ONE_TIME" },
];

export default function Plans() {
  const navigate = useNavigate();

  const [authed, setAuthed] = useState(null);
  const [credits, setCredits] = useState(null);

  // Current subscription (null = none active).
  const [currentSub, setCurrentSub] = useState(null);

  // PaymentModal state (existing one-time / new-subscription flow).
  const [paymentPlanKey, setPaymentPlanKey] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Change-plan confirmation modal state.
  const [changeTarget, setChangeTarget] = useState(null); // plan key the user wants to switch to
  const [changing, setChanging]         = useState(false);
  const [changeError, setChangeError]   = useState("");
  const [toast, setToast]               = useState("");

  /* ---------- Load user + current subscription on mount ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/site/user/logged-user");
        if (cancelled) return;
        if (res.data?.id) {
          setAuthed(true);
          setCredits(res.data?.account?.credit ?? 0);
          // Pull current subscription. /me returns null when there isn't one.
          try {
            const subRes = await axiosInstance.get("/site/subscription/me");
            if (cancelled) return;
            // The backend returns null body or {} when no sub exists.
            const sub = subRes.data && subRes.data.id ? subRes.data : null;
            // Any "alive" status should drive the manage-your-plan UI:
            //   ACTIVE    — normal subscriber
            //   CANCELED  — in grace window before period end
            //   PENDING   — newly created, awaiting payment confirmation
            //               (Stripe SCA flow or webhook not yet processed)
            //   PAST_DUE  — payment failed but still in retry window
            // EXPIRED and ERROR statuses are excluded — those users
            // effectively have no active subscription to manage.
            const ALIVE = ["ACTIVE", "CANCELED", "PENDING", "PAST_DUE"];
            if (sub && ALIVE.includes(sub.status)) {
              setCurrentSub(sub);
            } else {
              setCurrentSub(null);
            }
          } catch {
            if (!cancelled) setCurrentSub(null);
          }
        } else {
          setAuthed(false);
        }
      } catch {
        if (!cancelled) setAuthed(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currentPlanKey = currentSub?.plan || null;
  const currentRank    = TIER_RANK[currentPlanKey] || 0;
  const subStatus      = currentSub?.status || null;
  const isCanceledGrace = subStatus === "CANCELED";
  const isPending       = subStatus === "PENDING";
  const isPastDue       = subStatus === "PAST_DUE";
  // Disable plan-change attempts while the original subscription hasn't
  // finished being provisioned. The backend wouldn't be able to update it
  // at the provider cleanly until activation completes.
  const canChangePlan = subStatus === "ACTIVE" || subStatus === "CANCELED" || subStatus === "PAST_DUE";

  /* ---------- Click handlers ---------- */

  const handleSubscriptionClick = (planKey) => {
    if (authed === false) {
      navigate("/sign-up");
      return;
    }
    // No current sub → create flow.
    if (!currentPlanKey) {
      setPaymentPlanKey(planKey);
      setPaymentOpen(true);
      return;
    }
    // Same tier → silent no-op (button is disabled anyway).
    if (planKey === currentPlanKey) return;
    // Subscription still being provisioned → block with a friendly message.
    if (!canChangePlan) {
      setChangeTarget(planKey);
      setChangeError(
        isPending
          ? "Your subscription is still being activated. Please wait a minute and refresh, then try again."
          : "Your subscription can't be changed right now."
      );
      return;
    }
    // Different tier → open the change-plan confirmation modal.
    setChangeTarget(planKey);
    setChangeError("");
  };

  const handleOneTimeClick = (planKey) => {
    if (authed === false) {
      navigate("/sign-up");
      return;
    }
    setPaymentPlanKey(planKey);
    setPaymentOpen(true);
  };

  const confirmChangePlan = async () => {
    if (!changeTarget) return;
    setChanging(true);
    setChangeError("");
    try {
      const res = await axiosInstance.post("/site/subscription/change-plan", {
        plan: changeTarget,
      });
      const data = res.data || {};

      // PayPal flow: redirect to approve URL.
      if (data.requiresAction && data.approveUrl) {
        window.location.href = data.approveUrl;
        return;
      }

      // No-op (already on this tier).
      if (data.noOp) {
        setChangeTarget(null);
        setChanging(false);
        return;
      }

      // Stripe success: update local state and dismiss.
      if (data.subscription) {
        setCurrentSub(data.subscription);
        const verb =
          TIER_RANK[changeTarget] > currentRank ? "Upgraded" : "Downgraded";
        setToast(`${verb} to ${titleFor(changeTarget)}.`);
        setTimeout(() => setToast(""), 4000);
      }

      setChangeTarget(null);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Couldn't change your plan. Please try again.";
      setChangeError(msg);
    } finally {
      setChanging(false);
    }
  };

  const handleNewCredits = (newCredit) => {
    if (typeof newCredit === "number") setCredits(newCredit);
  };

  /* ---------- Render ---------- */

  return (
    <AppShell>
      <div className="px-4 sm:px-6 py-8 max-w-[1100px] mx-auto">

        <header className="text-center mb-10">
          <h1
            className="font-semibold mb-2"
            style={{ fontSize: "clamp(1.75rem, 4vw, 2.25rem)", color: "var(--txt)" }}
          >
            {currentPlanKey ? "Manage your plan." : "Choose your plan."}
          </h1>
          <p className="text-[14px] mx-auto" style={{ color: "var(--txt-2)", maxWidth: 560 }}>
            All LottoGPT plans are paid. Subscriptions include Scratch-Off
            Intelligence and a monthly credit allowance you can spend on AI lottery
            picks and premium scratch-off reports.
          </p>

          {authed && credits !== null && (
            <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium"
                 style={{
                   background: "var(--ink-dim)",
                   color: "var(--ink)",
                   border: "1px solid var(--ink-border)",
                 }}>
              <Icon name="coin" size={12} />
              Current balance: {credits} credit{credits === 1 ? "" : "s"}
            </div>
          )}

          {isPending && (
            <p className="mt-3 text-[12px]" style={{ color: "var(--gold)" }}>
              Your {titleFor(currentPlanKey)} subscription is being activated.
              Once payment is confirmed by your provider, you'll be able to
              change tiers from here. Refresh in a moment if this persists.
            </p>
          )}

          {isPastDue && (
            <p className="mt-3 text-[12px]" style={{ color: "var(--red)" }}>
              Your last payment failed. Update your payment method in Settings
              to keep your {titleFor(currentPlanKey)} benefits.
            </p>
          )}

          {isCanceledGrace && currentSub?.currentPeriodEnd && (
            <p className="mt-3 text-[12px]" style={{ color: "var(--txt-3)" }}>
              Your {titleFor(currentPlanKey)} subscription is set to cancel on{" "}
              {formatDate(currentSub.currentPeriodEnd)}. You can still upgrade,
              downgrade, or resubscribe before then.
            </p>
          )}
        </header>

        {/* ---------- Subscriptions ---------- */}
        <section className="mb-12">
          <div className="text-center mb-6">
            <div className="lg-label mb-2">Monthly subscriptions</div>
            <h2 className="font-semibold" style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)", color: "var(--txt)" }}>
              {currentPlanKey
                ? "Switch tier — proration is handled automatically."
                : "Pick a tier — credits and features included."}
            </h2>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-2">
            {SUBSCRIPTION_PLANS.map((p) => (
              <SubscriptionPlanCard
                key={p.key}
                plan={p}
                currentPlanKey={currentPlanKey}
                authed={authed}
                onClick={() => handleSubscriptionClick(p.key)}
              />
            ))}
          </div>
          <p className="text-[11px] text-center" style={{ color: "var(--txt-3)" }}>
            Subscriptions auto-renew monthly. Cancel any time from your dashboard.
          </p>
        </section>

        {/* ---------- One-time credit packs ---------- */}
        <section className="mb-12">
          <div className="text-center mb-6">
            <div className="lg-label mb-2">One-time credit packs</div>
            <h2 className="font-semibold" style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)", color: "var(--txt)" }}>
              Or buy credits as needed.
            </h2>
            <p className="text-[13px] mt-2 mx-auto" style={{ color: "var(--txt-2)", maxWidth: 520 }}>
              Credit packs are great for topping up between renewals. They
              <em> do not</em> include Scratch-Off Intelligence access — only
              subscriptions unlock that.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {ONE_TIME_PLANS.map((p) => (
              <OneTimePlanCard
                key={p.key}
                plan={p}
                authed={authed}
                onClick={() => handleOneTimeClick(p.key)}
              />
            ))}
          </div>
        </section>

        <Card variant="soft" className="mb-10">
          <div className="grid sm:grid-cols-3 gap-4">
            <TrustItem icon="shield-check" title="Secured by Stripe"
              body="We never store your card data. Payments go directly to Stripe or PayPal." />
            <TrustItem icon="refresh" title="Cancel anytime"
              body="Subscriptions can be cancelled in one click. You keep access through the period you paid for." />
            <TrustItem icon="brain" title="Same model, every plan"
              body="The model isn't behind a paywall — every plan uses the same prediction engine." />
          </div>
        </Card>

        <p className="text-[11px] text-center" style={{ color: "var(--txt-3)" }}>
          For entertainment purposes only · Must be 18+ in an eligible US state · Scratch-off
          outcomes are random and do not improve or guarantee your odds · See our refund policy
        </p>
      </div>

      {/* ---------- Modals ---------- */}

      {paymentOpen && (
        <PaymentModal
          isOpen={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          loadNewCredits={handleNewCredits}
          initialCreditPlan={paymentPlanKey}
          initialStep={2}
        />
      )}

      {changeTarget && (
        <ChangePlanConfirm
          fromPlanKey={currentPlanKey}
          toPlanKey={changeTarget}
          paymentMethod={currentSub?.paymentMethod}
          loading={changing}
          error={changeError}
          onCancel={() => { setChangeTarget(null); setChangeError(""); }}
          onConfirm={confirmChangePlan}
        />
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-full text-[13px] font-medium shadow-lg"
          style={{ background: "var(--teal)", color: "#FFFFFF" }}
        >
          {toast}
        </div>
      )}
    </AppShell>
  );
}

/* =================================================================== */
/* Subcomponents                                                       */
/* =================================================================== */

function SubscriptionPlanCard({ plan, currentPlanKey, authed, onClick }) {
  const featured = plan.featured;
  const isCurrent = currentPlanKey === plan.key;
  const currentRank = TIER_RANK[currentPlanKey] || 0;
  const thisRank = TIER_RANK[plan.key];

  let ctaLabel;
  if (authed === false) {
    ctaLabel = "Sign up to start";
  } else if (!currentPlanKey) {
    ctaLabel = "Start subscription";
  } else if (isCurrent) {
    ctaLabel = "Current plan";
  } else if (thisRank > currentRank) {
    ctaLabel = `Upgrade to ${plan.title}`;
  } else {
    ctaLabel = `Downgrade to ${plan.title}`;
  }

  return (
    <Card variant={featured ? "gold" : "default"} className="relative flex flex-col">
      {featured && (
        <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide"
              style={{ background: "var(--gold)", color: "#23364C" }}>
          Most popular
        </span>
      )}

      {isCurrent && (
        <span className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide"
              style={{ background: "var(--teal-dim)", color: "var(--teal)", border: "1px solid var(--teal-border)" }}>
          ✓ Your plan
        </span>
      )}

      <div className="font-medium mb-1" style={{ color: "var(--txt)" }}>
        {plan.title}
      </div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="font-semibold" style={{ fontSize: 32, color: "var(--txt)" }}>
          {plan.price}
        </span>
        <span className="text-[13px]" style={{ color: "var(--txt-2)" }}>
          {plan.priceSuffix}
        </span>
      </div>
      <p className="text-[12px] mb-4" style={{ color: "var(--txt-2)" }}>
        {plan.tagline}
      </p>

      <ul className="space-y-2 mb-5 flex-1">
        {plan.benefits.map((b) => (
          <li key={b} className="flex items-start gap-2 text-[13px]" style={{ color: "var(--txt-2)" }}>
            <Icon name="check" size={14} style={{ color: "var(--teal)", marginTop: 3, flexShrink: 0 }} />
            {b}
          </li>
        ))}
      </ul>

      <Button
        variant={isCurrent ? "outline" : featured ? "gold" : "ghost"}
        full
        disabled={isCurrent}
        onClick={onClick}
      >
        {ctaLabel}
      </Button>
    </Card>
  );
}

function OneTimePlanCard({ plan, authed, onClick }) {
  return (
    <Card className="flex flex-col">
      <div className="font-medium mb-1" style={{ color: "var(--txt)" }}>{plan.title}</div>
      <div className="font-semibold mb-2" style={{ fontSize: 28, color: "var(--txt)" }}>
        {plan.price}
      </div>
      <p className="text-[12px] mb-4" style={{ color: "var(--txt-2)" }}>{plan.tagline}</p>
      <ul className="space-y-2 mb-5 flex-1">
        {plan.benefits.map((b) => (
          <li key={b} className="flex items-start gap-2 text-[13px]" style={{ color: "var(--txt-2)" }}>
            <Icon name="check" size={14} style={{ color: "var(--teal)", marginTop: 3, flexShrink: 0 }} />
            {b}
          </li>
        ))}
      </ul>
      <Button variant="ghost" full onClick={onClick}>
        {authed === false ? "Sign up to buy" : "Buy credits"}
      </Button>
    </Card>
  );
}

function ChangePlanConfirm({ fromPlanKey, toPlanKey, paymentMethod, loading, error, onCancel, onConfirm }) {
  const fromRank = TIER_RANK[fromPlanKey] || 0;
  const toRank   = TIER_RANK[toPlanKey]   || 0;
  const direction = toRank > fromRank ? "Upgrade" : "Downgrade";
  const fromTitle = titleFor(fromPlanKey);
  const toTitle   = titleFor(toPlanKey);
  const isPaypal  = paymentMethod === "PAYPAL";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-[480px] rounded-2xl p-6"
        style={{
          // Use --bg (solid in both themes: #FFFFFF light, #23364C dark)
          // instead of --surf (which is rgba 5% white in dark mode and
          // would let the page content bleed through the modal).
          background: "var(--bg)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-soft)",
        }}
      >
        <h3 className="font-semibold mb-1" style={{ fontSize: 18, color: "var(--txt)" }}>
          {direction} to {toTitle}?
        </h3>
        <p className="text-[13px] mb-4" style={{ color: "var(--txt-2)" }}>
          You're switching from <strong style={{ color: "var(--txt)" }}>{fromTitle}</strong>{" "}
          to <strong style={{ color: "var(--txt)" }}>{toTitle}</strong>.
        </p>

        {isPaypal ? (
          <div
            className="rounded-lg p-3 mb-4 text-[12.5px] leading-relaxed"
            style={{
              background: "var(--gold-dim)",
              border: "1px solid var(--gold-border)",
              color: "var(--txt-2)",
            }}
          >
            <strong style={{ color: "var(--txt)" }}>You'll be redirected to PayPal</strong> to
            approve the plan change. After you confirm there, you'll come back here and
            the new tier will be active.
          </div>
        ) : (
          <div
            className="rounded-lg p-3 mb-4 text-[12.5px] leading-relaxed"
            style={{
              background: "var(--surf-2)",
              border: "1px solid var(--border)",
              color: "var(--txt-2)",
            }}
          >
            <strong style={{ color: "var(--txt)" }}>Proration is automatic.</strong>{" "}
            {direction === "Upgrade"
              ? "Stripe will credit the unused portion of your current period against the new price and charge you the difference on your next invoice."
              : "Stripe will credit the unused portion of your current tier and apply it to your next bill."}{" "}
            Your monthly credits already granted stay where they are.
          </div>
        )}

        {error && (
          <p
            className="rounded-lg px-3 py-2 mb-4 text-[12px]"
            style={{
              background: "var(--red-dim)",
              border: "1px solid var(--red-border)",
              color: "var(--red)",
            }}
          >
            {error}
          </p>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="gold" onClick={onConfirm} loading={loading}>
            {isPaypal ? "Continue to PayPal" : `Yes, ${direction.toLowerCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TrustItem({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: "var(--ink-dim)",
          border: "1px solid var(--ink-border)",
          color: "var(--ink)",
        }}
      >
        <Icon name={icon} size={18} />
      </div>
      <div>
        <div className="font-medium text-[13px] mb-0.5" style={{ color: "var(--txt)" }}>{title}</div>
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--txt-2)" }}>{body}</p>
      </div>
    </div>
  );
}

/* =================================================================== */
/* Helpers                                                             */
/* =================================================================== */

function titleFor(planKey) {
  switch (planKey) {
    case "PLUS":  return "Plus";
    case "PRO":   return "Pro";
    case "ELITE": return "Elite";
    default:      return planKey || "—";
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
